import { useMemo, useState } from 'react';
import { topicWall } from '@/mocks/homeData';
import SectionHead from './SectionHead';

function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)} 万`;
  return `${n}`;
}

const levelStyle: Record<string, string> = {
  爆: 'bg-primary-500 text-background-50 hover:bg-primary-600',
  热: 'bg-accent-500 text-secondary-950 hover:bg-accent-600',
  议: 'border border-secondary-300 bg-secondary-50 text-secondary-900 hover:border-secondary-500',
  新: 'border border-background-400 bg-background-50 text-foreground-700 hover:border-primary-400',
};

export default function TopicWall() {
  const [keyword, setKeyword] = useState('');
  const [activeLevel, setActiveLevel] = useState<'全部' | '爆' | '热' | '议' | '新'>('全部');

  const filtered = useMemo(() => {
    return topicWall.filter((t) => {
      const matchKeyword = keyword.trim() === '' || t.label.includes(keyword.trim());
      const matchLevel = activeLevel === '全部' || t.level === activeLevel;
      return matchKeyword && matchLevel;
    });
  }, [keyword, activeLevel]);

  return (
    <section id="hot-wall" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
        <SectionHead
          zone="分区 01"
          title="今日话题墙"
          desc="把正在被讨论的话题一次性铺开，越靠前、底色越重的，正在被越多人围观。"
          extra={
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <div className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-1 py-1">
                {(['全部', '爆', '热', '议', '新'] as const).map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => setActiveLevel(lv)}
                    className={`cursor-pointer rounded-full px-3 py-1.5 font-label text-xs font-medium transition-colors ${
                      activeLevel === lv
                        ? 'bg-foreground-950 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {lv === '全部' ? '全部' : `${lv}级`}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
                <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题关键词"
                  className="w-full bg-transparent font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none sm:w-40"
                />
              </div>
            </div>
          }
        />

        <div className="mt-7 flex flex-wrap gap-2.5 md:gap-3">
          {filtered.map((topic, idx) => (
            <a
              key={topic.id}
              href="#ranking"
              style={{ animationDelay: `${idx * 40}ms` }}
              className={`animate-rise-in flex cursor-pointer items-center gap-2 rounded-md px-3 py-2.5 transition-colors md:px-4 md:py-3 ${levelStyle[topic.level]}`}
            >
              <span className="font-heading text-sm font-bold md:text-base">{topic.label}</span>
              <span className="font-label text-[11px] opacity-80 md:text-xs">{formatCount(topic.count)}</span>
              {topic.level === '爆' && (
                <i className="ri-fire-fill text-xs text-accent-300" aria-hidden="true"></i>
              )}
            </a>
          ))}
          {filtered.length === 0 && (
            <div className="w-full rounded-lg border border-dashed border-background-300 py-10 text-center">
              <p className="font-label text-sm text-foreground-500">没有找到匹配的话题，换个关键词试试。</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}